import { useMemo } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortDirection } from '@cast/design-system';
import {
  OptimizationWorkload,
  WorkloadOptimizationState,
  WorkloadRuntime,
} from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WOOP_WORKLOADS_LIST_PAGINATED_QUERY_KEY = 'woop-workloads-list-paginated';

type Args = {
  clusterId: string;
  enabled?: boolean;
  pageLimit?: number;
  managementOptions?: WorkloadOptimizationState[];
  workloadIds?: string[];
  workloadNames?: string[];
  namespaces?: string[];
  scalingPolicyNames?: string[];
  kinds?: string[];
  sortField?: string;
  sortOrder?: SortDirection;
  anyContainerWithRuntime?: WorkloadRuntime;
};

const getPaginatedWorkloadsQueryKey = (
  params: {
    clusterId?: string;
    pageLimit?: number;
    managementOptions?: WorkloadOptimizationState[];
    workloadIds?: string[];
    workloadNames?: string[];
    namespaces?: string[];
    scalingPolicyNames?: string[];
    kinds?: string[];
    sortField?: string;
    sortOrder?: SortDirection;
    anyContainerWithRuntime?: WorkloadRuntime;
  } = {}
) =>
  [
    WOOP_WORKLOADS_LIST_PAGINATED_QUERY_KEY,
    params.clusterId,
    params.pageLimit,
    params.managementOptions,
    params.workloadIds,
    params.workloadNames,
    params.namespaces,
    params.scalingPolicyNames,
    params.kinds,
    params.sortField,
    params.sortOrder,
    params.anyContainerWithRuntime,
  ] as const;

const getPaginatedWorkloadsQueryOptions = ({
  clusterId,
  enabled = true,
  pageLimit = 50,
  managementOptions,
  workloadIds,
  workloadNames,
  namespaces,
  scalingPolicyNames,
  kinds,
  sortField,
  sortOrder,
  anyContainerWithRuntime,
}: Args) =>
  infiniteQueryOptions({
    queryKey: getPaginatedWorkloadsQueryKey({
      clusterId,
      pageLimit,
      managementOptions,
      workloadIds,
      workloadNames,
      namespaces,
      scalingPolicyNames,
      kinds,
      sortField,
      sortOrder,
      anyContainerWithRuntime,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.workloadOptimization.getWorkloads(
        {
          clusterId,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
          managementOptions,
          workloadIds,
          workloadNames,
          namespaces,
          scalingPolicyNames,
          kinds,
          sortField,
          sortOrder,
          anyContainerWithRuntime,
        },
        { timeout: 60_000 }
      );
      return data;
    },
    initialPageParam: '',
    getNextPageParam: ({ nextCursor }) =>
      nextCursor?.length ? nextCursor : undefined,
    enabled,
    refetchOnWindowFocus: false,
  });

export const usePaginatedWorkloadsQuery = (args: Args) => {
  const {
    isLoading,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useInfiniteQuery(getPaginatedWorkloadsQueryOptions(args));

  const workloads = useMemo<OptimizationWorkload[]>(
    () => flatMap(data?.pages, (page) => page.workloads) || [],
    [data]
  );

  return {
    workloads,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  };
};
